import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Link,
  Divider,
  IconButton,
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import XIcon from '@mui/icons-material/X';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { SectionVariable } from '../types';
import { useSettings } from '../Settings';
import { getAssetUrl, resolveAssetUrl } from '../api/assets';
import { getWhatsAppUrl } from '../utils/links';
import { useTranslation } from '../i18n';

interface FooterProps {
  variables?: SectionVariable;
  name?: string;
}

export function Footer({ variables }: FooterProps) {
  const { settings, app } = useSettings();
  const { t } = useTranslation();
  const logoSrc = variables?.image?.[0]
    ? resolveAssetUrl(variables.image[0], app)
    : getAssetUrl('images/logos/logo-white.svg', app);

  // Keys are stable identifiers; the visible labels come from `t`.
  // The legal sub-columns remain commented out as before.
  const footerLinks = [
    {
      titleKey: 'support' as const,
      title: t.footer.support,
      links: [
        { key: 'helpCenter' as const, label: t.footer.helpCenter },
        { key: 'customerService' as const, label: t.footer.customerService },
      ],
    },
  ];

  // const mobileLegalLinks = [
  //   { label: 'Termos', docKey: 'termsOfUse' as const },
  //   { label: 'Políticas de Privacidade', docKey: 'privacyPolicy' as const },
  //   { label: 'Preferências e Cookies', docKey: 'lgpd' as const },
  // ];

  // const legalText = `${settings.companyLegalName || settings.companyName || 'Empresa'}, inscrita no CNPJ nº ${settings.companyCNPJ || '00.000.000/0001-00'}, com sede na ${(settings.companyAddress || '').replace(/\n/g, ', ')}, atua como operadora móvel virtual (MVNO), credenciada à rede da operadora parceira, nos termos da regulamentação da Anatel. Planos, valores e condições estão sujeitos à disponibilidade de cobertura, análise cadastral e disposições contratuais. A velocidade anunciada corresponde à taxa máxima nominal, podendo variar conforme fatores técnicos, região e dispositivo utilizado. Os dados pessoais são tratados em conformidade com a Lei nº 13.709/2018 (LGPD). Consulte a Política de Privacidade para mais informações.`;

  const socialIcons = [
    { key: 'twitter', icon: <XIcon fontSize="small" />, href: settings.socials?.twitter },
    { key: 'linkedin', icon: <LinkedInIcon fontSize="small" />, href: settings.socials?.linkedin },
    { key: 'instagram', icon: <InstagramIcon fontSize="small" />, href: settings.socials?.instagram },
    { key: 'facebook', icon: <FacebookIcon fontSize="small" />, href: settings.socials?.facebook },
    { key: 'whatsapp', icon: <WhatsAppIcon fontSize="small" />, href: getWhatsAppUrl(settings.socials?.whatsapp) },
  ].filter(s => s.href);

  return (
    <Box
      component="footer"
      className="footer"
    >
      {/* ===== MOBILE FOOTER ===== */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          bgcolor: '#F5F5F5',
          color: '#333',
          py: 4,
          px: 3,
          borderTop: '1px solid #EAEAEA',
        }}
      >
        {/* Row 1: Logo (left) | Social Icons (right) */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Box
            component="img"
            src={getAssetUrl('images/logos/logo.svg', app)}
            alt="Logo"
            sx={{ height: 32 }}
          />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {socialIcons.map((s) => (
              <IconButton
                key={s.key}
                component="a"
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  color: '#1a1a1a',
                  p: 0.5,
                  '&:hover': { color: '#555' },
                }}
              >
                {s.icon}
              </IconButton>
            ))}
          </Box>
        </Box>

        {/* Row 2: Copyright centered */}
        <Typography
          sx={{
            color: '#878787',
            fontFamily: "'Figtree', sans-serif",
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 'normal',
            textAlign: 'center',
            mb: 3,
          }}
        >
          © {new Date().getFullYear()} {settings.companyName || t.footer.defaultCompany} - {t.footer.copyrightSuffix}
        </Typography>

        {/* Row 3: Divider */}
        <Box sx={{ height: '1px', background: '#E2E2E2', mb: 3 }} />

        {/* Row 4: Legal links in 3 columns (Ocultos temporariamente) */}
        {/*
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            mb: 3,
          }}
        >
          {mobileLegalLinks.map((item) => (
            <Link
              key={item.docKey}
              href="#"
              underline="hover"
              onClick={(e) => {
                e.preventDefault();
                const content = settings.legalDocs?.[item.docKey] || '';
                handleOpenModal(item.label, content);
              }}
              sx={{
                color: '#878787',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '0.75rem',
                fontWeight: 500,
                lineHeight: 'normal',
                textAlign: 'center',
              }}
            >
              {item.label}
            </Link>
          ))}
        </Box>
        */}

        {/* Row 5: Legal text (Oculto temporariamente) */}
        {/*
        <Typography
          sx={{
            color: '#B5B5B5',
            textAlign: 'justify',
            fontFamily: "'Figtree', sans-serif",
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: '141.726%',
          }}
        >
          {legalText}
        </Typography>
        */}
      </Box>

      {/* ===== DESKTOP FOOTER ===== */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          bgcolor: 'var(--color-primary-dark, #4A0099)',
          color: '#fff',
          py: 6,
          px: 4,
        }}
      >
        <Box maxWidth={1280} mx="auto">
          <Grid container spacing={4}>
            {/* Logo and company info */}
            <Grid size={{ md: 4 }}>
              <Box
                component="img"
                src={logoSrc}
                alt="Logo"
                sx={{ height: 32, mb: 2 }}
              />
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 2, fontSize: '0.8rem' }}>
                {settings.companyName || t.footer.defaultCompany}
              </Typography>
              {settings.companyAddress && (
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.5,
                    display: 'block',
                    whiteSpace: 'pre-line',
                    fontSize: '0.7rem',
                  }}
                >
                  {settings.companyAddress}
                </Typography>
              )}
              <Box display="flex" gap={1} mt={2}>
                {socialIcons.map((s) => (
                  <IconButton
                    key={s.key}
                    component="a"
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      color: '#FFFFFFB3',
                      '&:hover': { color: '#fff' },
                    }}
                  >
                    {s.icon}
                  </IconButton>
                ))}
              </Box>
            </Grid>

            {/* Footer link columns */}
            {footerLinks.map((col) => (
              <Grid size={{ md: 2 }} key={col.title}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  mb={2}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {col.title}
                </Typography>
                {col.links.map((link) => {
                  let href = '#';
                  let target: '_self' | '_blank' = '_self';

                  if (link.key === 'helpCenter') href = '#faq';
                  else if (link.key === 'customerService') {
                    href = getWhatsAppUrl(settings.socials?.whatsapp) || '#';
                    target = '_blank';
                  }

                  return (
                    <Link
                      key={link.key}
                      href={href}
                      target={target}
                      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                      display="block"
                      underline="hover"
                      sx={{
                        color: '#FFFFFF99',
                        fontSize: '0.75rem',
                        mb: 1,
                        '&:hover': { color: '#fff' },
                      }}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ borderColor: '#FFFFFF1A', my: 4 }} />

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.7rem', color: '#fff' }}>
              © {new Date().getFullYear()} {settings.companyName || t.footer.defaultCompany} - {t.footer.copyrightSuffix}.
            </Typography>
            {settings.companyCNPJ && (
              <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.7rem', color: '#fff' }}>
                {t.footer.cnpjLabel}: {settings.companyCNPJ}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

    </Box>
  );
}
